import { useEffect, useRef } from "react";

/**
 * Gọi lại API theo nhịp, nhưng CHỈ khi việc đó còn có nghĩa.
 *
 * Dùng cho những màn cần thấy dữ liệu phía đối diện mà không phải bấm Làm mới (hội thoại phản
 * ánh). Cố tình KHÔNG dùng WebSocket: phần "biết có việc mới" đã đi bằng kênh khác (tin nhóm
 * Zalo), ở đây chỉ cần dữ liệu tự về trong lúc người ta đang mở màn.
 *
 * Bốn thứ hook này lo — thiếu bất kỳ cái nào là poll thành gánh nặng chứ không phải tiện nghi:
 *
 * 1. `setTimeout` ĐỆ QUY, không `setInterval`: nhịp sau chỉ hẹn khi nhịp trước đã xong. Với
 *    interval, API chậm hơn nhịp là các request chồng lên nhau, càng chậm càng dồn.
 * 2. Tab ẩn thì DỪNG; hiện lại thì gọi ngay một lần rồi vào nhịp. Đây là case "treo tab" phổ
 *    biến nhất, và cũng chính là thao tác người dùng đang phải làm tay (rời trang rồi quay lại).
 * 3. Người rời máy (không chuột/bàn phím/scroll/touch) quá `idleAfterMs` thì DỪNG HẲN, không
 *    phải giãn nhịp — không ai xem thì con số đúng là 0 request. Có tương tác lại là nối tiếp.
 * 4. Đang gõ thì LÙI nhịp (đỡ giật), nhưng chỉ lùi tối đa `maxSkips` lần liên tiếp, kẻo người
 *    gõ liên tục vài phút thì không bao giờ nhận được tin mới.
 *
 * Lỗi thì tự giãn nhịp theo `backoffMs`; nơi gọi cho `stopped = true` (401/403, phiên hết hạn)
 * là dừng hẳn. `fn` KHÔNG được tự bắn toast — đây là request ngầm, lỗi mạng mỗi nhịp sẽ thành
 * hàng chục toast đè lên màn.
 *
 * @param {() => Promise<any>} fn Việc cần chạy mỗi nhịp (thường là fetch bản silent).
 * @param {object}   options
 * @param {boolean}  options.enabled      false = không chạy (chưa mở chi tiết chẳng hạn).
 * @param {boolean}  options.stopped      true = dừng hẳn, không nối lại (hết phiên/mất quyền).
 * @param {number}   options.intervalMs   Nhịp cơ bản.
 * @param {number}   options.idleAfterMs  Không tương tác quá lâu thì dừng.
 * @param {number[]} options.backoffMs    Nhịp sau lỗi thứ 1, 2, 3...
 * @param {number}   options.typingGraceMs Vừa gõ trong khoảng này thì lùi nhịp.
 * @param {number}   options.maxSkips     Số lần lùi liên tiếp tối đa.
 */
export function usePolling(
  fn,
  {
    enabled = false,
    stopped = false,
    intervalMs = 60000,
    // Nhịp 60s nên mốc "rời máy" phải rộng hơn, không thì đọc yên 2 phút là poll tự ngủ.
    idleAfterMs = 300000,
    backoffMs = [60000, 120000, 300000],
    typingGraceMs = 3000,
    maxSkips = 2,
  } = {}
) {
  // Giữ fn trong ref: nơi gọi luôn truyền arrow mới mỗi lần render, để vào deps là hẹn lại vô hạn.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const timerRef = useRef(null);
  const runningRef = useRef(false);
  const errorCountRef = useRef(0);
  const skipCountRef = useRef(0);
  const lastActivityRef = useRef(Date.now());
  const lastTypingRef = useRef(0);

  useEffect(() => {
    if (!enabled || stopped) return;

    let alive = true;

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const isVisible = () =>
      typeof document === "undefined" || document.visibilityState === "visible";

    const isIdle = () => Date.now() - lastActivityRef.current > idleAfterMs;

    const schedule = (delay) => {
      clearTimer();
      if (!alive) return;
      timerRef.current = setTimeout(tick, delay);
    };

    async function tick() {
      if (!alive) return;

      // Tab ẩn hoặc người đã rời máy -> không hẹn nhịp mới. `visibilitychange`/activity sẽ
      // đánh thức lại; nhờ vậy trạng thái "ngủ" là 0 request chứ không phải request thưa.
      if (!isVisible() || isIdle()) {
        clearTimer();
        return;
      }

      // Đang gõ -> lùi nhịp cho khỏi giật, nhưng có trần để không bỏ rơi tin mới.
      if (Date.now() - lastTypingRef.current < typingGraceMs && skipCountRef.current < maxSkips) {
        skipCountRef.current += 1;
        schedule(intervalMs);
        return;
      }
      skipCountRef.current = 0;

      if (runningRef.current) {
        schedule(intervalMs);
        return;
      }

      runningRef.current = true;
      try {
        await fnRef.current();
        errorCountRef.current = 0;
      } catch (e) {
        // Lỗi ở đây là chuyện của nhịp ngầm: chỉ giãn nhịp, không làm ồn màn hình.
        errorCountRef.current += 1;
      } finally {
        runningRef.current = false;
      }

      if (!alive) return;

      const delay = errorCountRef.current
        ? backoffMs[Math.min(errorCountRef.current - 1, backoffMs.length - 1)]
        : intervalMs;
      schedule(delay);
    }

    /** Người quay lại (mở tab, chạm màn hình) -> gọi ngay một lần cho khỏi phải chờ hết nhịp. */
    const wakeUp = () => {
      lastActivityRef.current = Date.now();
      if (!alive || !isVisible() || timerRef.current || runningRef.current) return;
      schedule(0);
    };

    const onActivity = () => {
      lastActivityRef.current = Date.now();
      // Đang ngủ vì idle -> đánh thức. Đang chạy bình thường thì chỉ ghi nhận mốc hoạt động.
      if (!timerRef.current && !runningRef.current) schedule(intervalMs);
    };

    const onTyping = () => {
      lastTypingRef.current = Date.now();
      lastActivityRef.current = Date.now();
    };

    const onVisibility = () => {
      if (isVisible()) wakeUp();
      else clearTimer();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", wakeUp);
    window.addEventListener("online", wakeUp);
    window.addEventListener("mousemove", onActivity, { passive: true });
    window.addEventListener("mousedown", onActivity, { passive: true });
    window.addEventListener("scroll", onActivity, { passive: true });
    window.addEventListener("touchstart", onActivity, { passive: true });
    window.addEventListener("keydown", onTyping);

    lastActivityRef.current = Date.now();
    schedule(intervalMs);

    return () => {
      alive = false;
      clearTimer();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", wakeUp);
      window.removeEventListener("online", wakeUp);
      window.removeEventListener("mousemove", onActivity);
      window.removeEventListener("mousedown", onActivity);
      window.removeEventListener("scroll", onActivity);
      window.removeEventListener("touchstart", onActivity);
      window.removeEventListener("keydown", onTyping);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, stopped, intervalMs, idleAfterMs, typingGraceMs, maxSkips]);
}
